import { UUID } from 'crypto';
import { isDateBefore, isDateSame, isDateSameOrAfter, isDateSameOrBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { TankTypeEnum } from 'src/types/ViewModel/AolEnums';
import { AolDataItem, AolGroup } from 'src/types/ViewModel/AolItem';
import { AppFeatureVessel } from 'src/types/ViewModel/AppFeature/AppFeatureVessel';
import { BiofuelGrade } from 'src/types/ViewModel/Bunkering/BiofuelGrade';
import { BioFuelType } from 'src/types/ViewModel/Bunkering/BioFuelType';
import { Bunkering } from 'src/types/ViewModel/Bunkering/Bunkering';
import { BunkeringEvent, BunkeringStatusEnum } from 'src/types/ViewModel/Bunkering/BunkeringEvent';
import { BunkeringStatus } from 'src/types/ViewModel/Bunkering/BunkeringStatus';
import { FuelROB } from 'src/types/ViewModel/Bunkering/FuelROB';
import { FuelRobVM } from 'src/types/ViewModel/Bunkering/FuelRobVM';
import { FuelType } from 'src/types/ViewModel/Bunkering/FuelType';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { BUNKERING_INPUT_FIELD_KEYS } from 'src/enums/bunkering/bunkeringInputFieldKeys';

export const BIO_DEFAULT_DENSITY = 0.8919;

export enum BunkeringPageTabs {
    registration = 0,
    rob = 1,
}

export enum BunkerRegTabsEnum {
    fuelReceived = 0,
    status = 1,
}

export const fuelTankTypes: number[] = [
    TankTypeEnum.HeavyFuelOil,
    TankTypeEnum.MarineGasOil,
    TankTypeEnum.LightFuelOil,
    TankTypeEnum.VirtualHFOTank,
    TankTypeEnum.VirtualMGOTank,
];

export const getFuelTanks = (vessel: Vessel): Tank[] => {
    return vessel?.nonCargoTanks?.filter((tank) => fuelTankTypes.includes(tank.tankType));
};

export const getBunkeringReference = (
    fuelType: string,
    portUnCode: string | null,
    portName: string | null,
    bunkeringDateTime: Date,
    index?: number | null
): string => {
    return `${fuelType} ${portUnCode ?? portName?.substring(0, 3) ?? 'UNKNOWN'} ${bunkeringDateTime.getFullYear()}-${index ?? 1}`;
};

export const hasBunkeringRemainingOnBoard = (currentStatus: BunkeringStatus): boolean => {
    return currentStatus.status !== BunkeringStatusEnum.Debunkered && currentStatus.status !== BunkeringStatusEnum.Spent;
};

export const getLatestStatus = (statuses: BunkeringStatus[]): BunkeringStatus | null => {
    return statuses.length > 0 ? statuses.reduce((prev, current) => (prev.activeFromDate > current.activeFromDate ? prev : current)) : null;
};

export const getBunkeringStatusAtDate = (bunkering: Bunkering, date: Date): BunkeringStatus | null => {
    if (!bunkering.bunkeringStatuses || bunkering.bunkeringStatuses.length === 0) return null;
    const statusesBeforeDate = bunkering.bunkeringStatuses.filter((status) => status.activeFromDate <= date);
    const latestStatus = statusesBeforeDate.reduce((prev, current) => (prev?.activeFromDate > current?.activeFromDate ? prev : current), null);
    return latestStatus;
};

export const isBunkeringOnboardAtDate = (bunkering: Bunkering, date: Date): boolean => {
    const status = getBunkeringStatusAtDate(bunkering, date);
    if (!status && isDateSameOrBefore(bunkering.bunkeringDateTime, date)) return true;
    return status && status.status !== BunkeringStatusEnum.Unknown && hasBunkeringRemainingOnBoard(status);
};

export const isBunkeringUsableAtDate = (bunkering: Bunkering, date: Date): boolean => {
    const status = getBunkeringStatusAtDate(bunkering, date);
    return status && (status.status === BunkeringStatusEnum.Approved || status.status === BunkeringStatusEnum.InUse);
};

export const getBunkeringsOnboardAtDate = (bunkerings: Bunkering[], date: Date): Bunkering[] => {
    return bunkerings.filter((bunkering) => isBunkeringOnboardAtDate(bunkering, date));
};

const getDateSpanForAolGroup = (date: Date): { fromDate: Date; toDate: Date } => {
    const fromDate = new Date(date);
    fromDate.setDate(fromDate.getDate() - 1);
    fromDate.setHours(12, 0, 0, 0);
    const toDate = new Date(date);
    toDate.setHours(12, 0, 0, 0);
    return { fromDate, toDate };
};

export const getBunkeringEventsInAolGroupPeriod = (date: Date, bunkeringEvents: BunkeringEvent[]) => {
    const { fromDate, toDate } = getDateSpanForAolGroup(date);
    return bunkeringEvents.filter(
        (bunkeringEvent) =>
            isDateSameOrAfter(bunkeringEvent.bunkeringDateTimeLocal, fromDate) && isDateBefore(bunkeringEvent.bunkeringDateTimeLocal, toDate)
    );
};

export const getBunkeringEventsInAolPeriod = (fromDate: Date, toDate: Date, bunkeringEvents: BunkeringEvent[]) => {
    return bunkeringEvents.filter(
        (bunkeringEvent) =>
            isDateSameOrAfter(bunkeringEvent.bunkeringDateTimeLocal, fromDate) && isDateBefore(bunkeringEvent.bunkeringDateTimeLocal, toDate)
    );
};

export const getBunkeringsInAolGroup = (date: Date, bunkerings: Bunkering[]): Bunkering[] => {
    const { fromDate, toDate } = getDateSpanForAolGroup(date);
    return bunkerings?.filter(
        (bunkering) => isDateSameOrAfter(bunkering.bunkeringDateTime, fromDate) && isDateBefore(bunkering.bunkeringDateTime, toDate)
    );
};

export const getBunkeringsInAolPeriod = (fromDate: Date, toDate: Date, bunkerings: Bunkering[]): Bunkering[] => {
    return bunkerings?.filter(
        (bunkering) => isDateSameOrAfter(bunkering.bunkeringDateTime, fromDate) && isDateBefore(bunkering.bunkeringDateTime, toDate)
    );
};

export const getPreviousAolItem = (currentAolItem: AolDataItem, aolItems: AolDataItem[]): AolDataItem | null => {
    const currentIndex = aolItems
        .sort((a, b) => (isDateSameOrBefore(a.reportToTimeLocalTime, b.reportToTimeLocalTime) ? -1 : 1))
        .findIndex((item) => item.id && item?.id === currentAolItem?.id);
    return currentIndex > 0
        ? aolItems.sort((a, b) => (isDateSameOrBefore(a.reportToTimeLocalTime, b.reportToTimeLocalTime) ? -1 : 1))[currentIndex - 1]
        : null;
};

const check = (bunkering: Bunkering, tankId: number, previousRobs: FuelROB[], currentRobs: FuelROB[], currentBunkerings: Bunkering[]): boolean => {
    // an ROB exists for the tank and the ROB is greater than 0 in previous ROBs
    const inPreviousRob = previousRobs?.some((rob) => rob.bunkeringGuid === bunkering.bunkeringGuid && rob.tankId === tankId && rob.rob > 0);
    // fuel was transfered in this Aol period so that it is in the tank for current period, but not previous
    const inCurrentRob = currentRobs?.some((rob) => rob.bunkeringGuid === bunkering.bunkeringGuid && rob.tankId === tankId && rob.rob > 0);
    // or the fuel was bunkered in the current Aol period
    const newBunkering = currentBunkerings.some(
        (b) => b.bunkeringGuid === bunkering.bunkeringGuid && b.bunkerReceivedPerTanks.some((tank) => tank.tankId === tankId)
    );

    return inPreviousRob || inCurrentRob || newBunkering;
};

export const getContentsInTankAtDate = (
    bunkerings: Bunkering[],
    tankId: number,
    date: Date,
    aolGroup: AolGroup,
    allAolDataItems: AolDataItem[],
    robs: FuelROB[],
    appFeatureVessel?: AppFeatureVessel | null
): Bunkering[] => {
    if (aolGroup?.aolDataItems?.length > 0) {
        const firstAolItem = aolGroup.aolDataItems[0];
        const noonReportIds = aolGroup.aolDataItems.map((item) => item.noonReport?.metadata?.noonReportId).filter((id) => id !== undefined);
        const previousAolItem = getPreviousAolItem(firstAolItem, allAolDataItems);
        const previousRobs = robs?.filter((rob) => rob.noonReportId && rob.noonReportId === previousAolItem?.noonReport?.metadata?.noonReportId);
        const currentRobs = robs?.filter((rob) => rob.noonReportId && noonReportIds.includes(rob.noonReportId));
        const currentBunkerings = getBunkeringsInAolGroup(date, bunkerings);
        if (appFeatureVessel && appFeatureVessel.fromDate && isDateSame(appFeatureVessel.fromDate, date, 'day')) {
            return bunkerings.filter(
                (bunkering) =>
                    // filter out bunkering events which are registered as spent and were bunkered before the feature activation date
                    !(
                        getBunkeringStatusAtDate(bunkering, date)?.status === BunkeringStatusEnum.Spent &&
                        isDateBefore(bunkering.bunkeringDateTime, appFeatureVessel.fromDate)
                    ) &&
                    isDateSameOrBefore(bunkering.bunkeringDateTime, date) &&
                    (bunkering.bunkerReceivedPerTanks.some((tank) => tank.tankId === tankId) ||
                        check(bunkering, tankId, previousRobs, currentRobs, currentBunkerings))
            );
        }

        const _bunkerings = bunkerings.filter((bunkering) => check(bunkering, tankId, previousRobs, currentRobs, currentBunkerings));

        return _bunkerings;
    }
    return bunkerings.filter(
        (bunkering) => isBunkeringOnboardAtDate(bunkering, date) && bunkering.bunkerReceivedPerTanks.some((tank) => tank.tankId === tankId)
    );
};

export const getTankTypeDisplayName = (tankType: TankTypeEnum): string => {
    switch (tankType) {
        case TankTypeEnum.Unknown:
            return 'Unknown';
        case TankTypeEnum.Cargo:
            return 'Cargo';
        case TankTypeEnum.WaterBallast:
            return 'Water Ballast';
        case TankTypeEnum.HeavyFuelOil:
            return 'Heavy Fuel Oil';
        case TankTypeEnum.MarineGasOil:
            return 'Marine Gas Oil';
        case TankTypeEnum.LubOil:
            return 'Lubricating Oil';
        case TankTypeEnum.FreshWater:
            return 'Fresh Water';
        case TankTypeEnum.TechnicalFreshWater:
            return 'Technical Fresh Water';
        case TankTypeEnum.MiscellaneousTanks:
            return 'Miscellaneous Tanks';
        case TankTypeEnum.LightFuelOil:
            return 'Light Fuel Oil';
        case TankTypeEnum.VirtualHFOTank:
            return 'Virtual HFO Tank';
        case TankTypeEnum.VirtualMGOTank:
            return 'Virtual MGO Tank';
        default:
            return '';
    }
};

export enum BunkerRegSectionType {
    OperationalData = 1,
    BDN = 2,
    FTA = 3,
    ProofOfSustainability = 4,
}

export interface BunkeringInputField {
    key: string;
    label: string;
    bioFuel: boolean;
    inputType: string;
    section: BunkerRegSectionType;
    min?: number;
    max?: number;
    unit?: string;
    options?: string[];
    selectDefaultOption?: string;
}

const BIO_BLEND_FOSSIL_FUEL_TYPE_OPTIONS = [FuelType[FuelType.LSF], FuelType[FuelType.LGO]];

export const bunkeringInputFields: BunkeringInputField[] = [
    { key: BUNKERING_INPUT_FIELD_KEYS.BDN_REFERENCE, label: 'BDN Reference', bioFuel: false, inputType: 'text', section: BunkerRegSectionType.BDN },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.FUEL_TYPE,
        label: 'Fuel Type',
        bioFuel: false,
        inputType: 'select',
        section: BunkerRegSectionType.OperationalData,
        options: Object.keys(FuelType).filter((v) => isNaN(Number(v))),
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.TOTAL_RECEIVED_QUANTITY,
        label: 'Total Received Quantity',
        bioFuel: false,
        inputType: 'number',
        section: BunkerRegSectionType.BDN,
        min: 0,
        max: 3000,
        unit: 'MT',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.SULPHUR,
        label: 'Sulphur',
        bioFuel: false,
        inputType: 'number',
        section: BunkerRegSectionType.BDN,
        min: 0,
        max: 10,
        unit: '%',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.FUEL_VISCOSITY,
        label: 'Fuel Viscosity',
        bioFuel: false,
        inputType: 'number',
        section: BunkerRegSectionType.FTA,
        min: 0.5,
        max: 500,
        unit: 'mm²/s @40°C/50°C',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.BDN_LITER_WEIGHT,
        label: 'BDN Liter Weight',
        bioFuel: false,
        inputType: 'number',
        section: BunkerRegSectionType.BDN,
        min: 0.5,
        max: 1.5,
        unit: 'kg/L @15°C',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.FTA_LITER_WEIGHT,
        label: 'FTA Liter Weight',
        bioFuel: false,
        inputType: 'number',
        section: BunkerRegSectionType.FTA,
        min: 0.5,
        max: 1.5,
        unit: 'kg/L @15°C',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.WATER_CONTENT,
        label: 'Water Content',
        bioFuel: false,
        inputType: 'number',
        section: BunkerRegSectionType.FTA,
        min: 0,
        max: 50,
        unit: '%',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.BIO_FUEL_TYPE,
        label: 'Biofuel Type',
        bioFuel: true,
        inputType: 'select',
        section: BunkerRegSectionType.BDN,
        options: Object.keys(BioFuelType).filter((v) => isNaN(Number(v))),
        selectDefaultOption: 'Select biofuel type',
    },
    /**
     * This field has been deactivated and functionally replaced by biofuelGrade
     */
    // {
    //     key: BUNKERING_INPUT_FIELD_KEYS.BIO_BLEND,
    //     label: 'Bio Blend',
    //     bioFuel: true,
    //     inputType: 'number',
    //     section: BunkerRegSectionType.BDN,
    //     min: 0,
    //     max: 100,
    //     unit: '%',
    // },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_VOLUME,
        label: 'Volume Biofuel',
        bioFuel: true,
        inputType: 'number',
        section: BunkerRegSectionType.ProofOfSustainability,
        min: 0,
        max: 3000,
        unit: 'm³',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_MASS,
        label: 'Biofuel quantity (portion of blend)',
        bioFuel: true,
        inputType: 'number',
        section: BunkerRegSectionType.ProofOfSustainability,
        min: 0,
        max: 3000,
        unit: 'MT',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_ENERGY_CONTENT,
        label: 'Bio Energy Content',
        bioFuel: true,
        inputType: 'number',
        section: BunkerRegSectionType.ProofOfSustainability,
        unit: 'MJ',
        min: 1,
        max: 30000000,
    },
    /**
     * This field has been deactivated until further notice (IMO regulations may come in 2026 or later)
     */
    // {
    //     key: BUNKERING_INPUT_FIELD_KEYS.LOWER_HEATING_VALUE,
    //     label: 'IMO Lower Heating Value',
    //     bioFuel: true,
    //     inputType: 'number',
    //     section: BunkerRegSectionType.ProofOfSustainability,
    //     min: 20,
    //     max: 60,
    //     unit: 'MJ/kg',
    // },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.EU_LOWER_CALORIFIC_VALUE,
        label: 'EU Lower Calorific Value',
        bioFuel: true,
        inputType: 'number',
        section: BunkerRegSectionType.ProofOfSustainability,
        min: 20,
        max: 60,
        unit: 'MJ/kg',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.EU_GHG_EMISSIONS,
        label: 'EU GHG Emissions intensity',
        bioFuel: true,
        inputType: 'number',
        section: BunkerRegSectionType.ProofOfSustainability,
        min: 1,
        max: 40,
        unit: 'gCO₂eq/MJ',
    },
    /**
     * This field has been deactivated until further notice (IMO regulations may come in 2026 or
     * later)
     */
    // {
    //     key: BUNKERING_INPUT_FIELD_KEYS.IMO_GHG_EMISSIONS,
    //     label: 'IMO GHG Emissions intensity',
    //     bioFuel: true,
    //     inputType: 'number',
    //     section: BunkerRegSectionType.ProofOfSustainability,
    //     min: 1,
    //     max: 99,
    //     unit: 'gCO₂eq/MJ',
    // },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.SUSTAINABILITY_REFERENCE,
        label: 'Sustainability Reference',
        bioFuel: true,
        inputType: 'text',
        section: BunkerRegSectionType.ProofOfSustainability,
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_GRADE,
        label: 'Biofuel Grade',
        bioFuel: true,
        inputType: 'select',
        section: BunkerRegSectionType.BDN,
        options: Object.keys(BiofuelGrade).filter((v) => isNaN(Number(v))),
        selectDefaultOption: 'Select biofuel grade',
    },
    {
        key: BUNKERING_INPUT_FIELD_KEYS.BIO_BLEND_FOSSIL_FUEL_TYPE,
        label: 'Bio Blend Fossil Fuel Type',
        bioFuel: true,
        inputType: 'select',
        section: BunkerRegSectionType.BDN,
        options: BIO_BLEND_FOSSIL_FUEL_TYPE_OPTIONS,
        selectDefaultOption: 'Select fossil fuel type',
    },
];

export const getBunkeringStatusEnumDisplayName = (status: BunkeringStatusEnum, statusActiveFrom: Date, approvedForUseAt: Date | null): string => {
    switch (status) {
        case BunkeringStatusEnum.Unknown:
            return 'New';
        case BunkeringStatusEnum.Registered:
            return 'Awaiting approval';
        case BunkeringStatusEnum.Approved:
            return 'Approved';
        case BunkeringStatusEnum.Rejected:
            return 'Rejected';
        case BunkeringStatusEnum.InUse:
            return !approvedForUseAt || statusActiveFrom < approvedForUseAt ? 'Approval bypassed' : 'In Use';
        case BunkeringStatusEnum.Spent:
            return 'Spent';
        case BunkeringStatusEnum.Debunkered:
            return 'Debunkered';
        default:
            return 'New';
    }
};

export const getOneYearBefore = (date: Date): Date => {
    const oneYearBefore = new Date(date);
    oneYearBefore.setFullYear(oneYearBefore.getFullYear() - 1);
    return oneYearBefore;
};

export const getThreeYearsBefore = (date: Date): Date => {
    const threeYearsBefore = new Date(date);
    threeYearsBefore.setFullYear(threeYearsBefore.getFullYear() - 3);
    return threeYearsBefore;
};

export const checkBunkeringStatusAfterRobUpdate = (bunkering: Bunkering, fuelRobData: FuelROB[]): BunkeringStatusEnum => {
    const allRobData = fuelRobData.filter((rob) => rob.bunkeringGuid === bunkering.bunkeringGuid);
    if (!allRobData || !allRobData.some((rob) => rob.rob > 0)) {
        return BunkeringStatusEnum.Spent;
    } else if (allRobData.some((rob) => rob.consumed > 0)) return BunkeringStatusEnum.InUse;
    return bunkering.currentStatus.status;
};

export const getAolGroupForDateTime = (date: Date, aolGroups: AolGroup[]): AolGroup | null => {
    return aolGroups.find((aolGroup) => isDateSameOrAfter(aolGroup.aolDate, date)) || null;
};

export const getMaxWeight = (tank: Tank, literWeight: number) => {
    const maxWeight = tank.volumeAt98Percent * (literWeight ?? 1);
    return Number(maxWeight.toFixed(3));
};

export const getNumUnregisteredBunkeringEvents = (
    bunkeringEvents: BunkeringEvent[],
    bunkerings: Bunkering[],
    selectedBunkeringDate: Date
): [number, number] => {
    if (bunkerings && bunkeringEvents) {
        const _bunkeringEvents = bunkeringEvents.filter(
            (b) =>
                isDateSameOrBefore(b.bunkeringDateTimeLocal, selectedBunkeringDate, 'day') &&
                isDateSameOrAfter(b.bunkeringDateTimeLocal, getOneYearBefore(selectedBunkeringDate))
        );
        const _numUnregisteredBunkerings = _bunkeringEvents.filter(
            (b) => !b.debunkering && !bunkerings.find((b2) => b2.eventId === b.eventID)
        ).length;
        const _numUnregisteredDebunkerings = _bunkeringEvents.filter(
            (b) => b.debunkering && !bunkerings.find((b2) => b2.debunkeringEventIds.includes(b.eventID as UUID))
        ).length;
        return [_numUnregisteredBunkerings, _numUnregisteredDebunkerings];
    }
    return [0, 0];
};

export const getBunkeringForBunkeringEvent = (bunkeringEvent: BunkeringEvent, bunkerings: Bunkering[]): Bunkering | null => {
    return bunkerings.find(
        (b) =>
            b.eventId === bunkeringEvent.eventID &&
            (b.fuelType === bunkeringEvent.fuelType || (b.fuelType === FuelType.LSF && bunkeringEvent.fuelType === FuelType.HFO))
    );
};

export const canUserChangeStatus = (currentBunkeringStatus: BunkeringStatus): boolean => {
    return currentBunkeringStatus?.status !== BunkeringStatusEnum.Debunkered && currentBunkeringStatus?.status !== BunkeringStatusEnum.Spent;
};

export const validateTotalReceivedVsAllocatedFuel = (bunkering: Bunkering): boolean => {
    if (bunkering?.totalReceivedQuantity === null || bunkering?.totalReceivedQuantity === undefined || bunkering?.totalReceivedQuantity === 0) {
        return true;
    }
    const totalAllocatedToTanks = parseFloat(bunkering.bunkerReceivedPerTanks.reduce((acc, tank) => acc + tank.bunkerReceivedMt, 0).toFixed(2));

    return totalAllocatedToTanks === parseFloat(bunkering.totalReceivedQuantity?.toFixed(2));
};

export const hasConsOrLossForTankAndBunkering = (tankId: number, bunkeringGuid: UUID, fuelrobVM: FuelRobVM): boolean => {
    return fuelrobVM.fuelRobColumns.some((c) =>
        c.fuelRobs.some((f) => f.bunkeringGuid === bunkeringGuid && f.tankId === tankId && (f.consumed > 0 || f.loss < 0))
    );
};
